/**
 * One starred account is the default selected when the app loads.
 * Stored on users.app_settings alongside selectedAccountId (last account used).
 * starredAccountPaper disambiguates a shared AccountID across SIM and live.
 */

export function findStarredAccount(accounts, appSettings) {
  const id = appSettings?.starredAccountId;
  if (id == null || id === '' || !Array.isArray(accounts) || accounts.length === 0) return null;
  const matches = accounts.filter((account) => String(account?.AccountID) === String(id));
  if (matches.length === 0) return null;
  if (typeof appSettings?.starredAccountPaper === 'boolean') {
    return matches.find((account) => !!account.isPaperTrading === appSettings.starredAccountPaper) || null;
  }
  return matches[0];
}

export function isStarredAccount(appSettings, account) {
  if (account?.AccountID == null || appSettings?.starredAccountId == null || appSettings.starredAccountId === '') {
    return false;
  }
  if (String(appSettings.starredAccountId) !== String(account.AccountID)) return false;
  if (typeof appSettings.starredAccountPaper === 'boolean') {
    return !!account.isPaperTrading === appSettings.starredAccountPaper;
  }
  return true;
}

/** Set `account` as the only default, or clear the default when account is null. */
export function withStarredAccount(appSettings, account) {
  const next = { ...(appSettings || {}) };
  if (account?.AccountID == null || account.AccountID === '') {
    delete next.starredAccountId;
    delete next.starredAccountPaper;
    return next;
  }
  next.starredAccountId = account.AccountID;
  next.starredAccountPaper = !!account.isPaperTrading;
  return next;
}

/**
 * Account to prefer once the broker account list is known.
 * The star wins when nothing is selected yet (`manualSelection` false).
 * Once an account is selected, last-used selectedAccountId stays in charge.
 * Returns null when neither a star nor a last-used id matches, so the caller can fall through.
 */
export function pickPreferredAccount({ accounts, appSettings, manualSelection }) {
  const list = Array.isArray(accounts) ? accounts : [];
  const starred = findStarredAccount(list, appSettings);
  if (!manualSelection && starred) return starred;
  const preferredId = appSettings?.selectedAccountId;
  if (preferredId == null || preferredId === '') return null;
  return list.find((account) => String(account?.AccountID) === String(preferredId)) || null;
}
